import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { BUS_MAINTENANCE_STAY_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import {
  clearBusMaintenanceStay,
  getBusMaintenanceStay,
  putBusMaintenanceStay,
} from '../../../../actions/mechanical-maintenance/BusMaintenanceStay';
import BusMaintenanceStayForm from './BusMaintenanceStayForm';
import { tzNormalizeDate } from '../../../../utils/date';
import { TIMESTAMP_FORMAT } from '../../../../config/locale';
import { BUS_MAINTENANCE_STATUS_TYPE } from '../../../../config/constants';

const EditBusMaintenanceStay = ({
  breadcrumbs,
  dispatchClearBusMaintenanceStay,
  dispatchGetBusMaintenanceStay,
  dispatchPutBusMaintenanceStay,
  match: {
    params: { id: busMaintenanceStayId },
  },
  loading,
  busMaintenanceStay,
}) => {
  useLayoutEffect(() => {
    dispatchGetBusMaintenanceStay({ busMaintenanceStayId });

    return () => dispatchClearBusMaintenanceStay();
  }, []);

  const onSubmit = (formValues) => {
    const newFormValues = { ...formValues };

    newFormValues.registeredBusId = formValues.registeredBusId.value;
    newFormValues.originCityId = formValues.originCityId.value;

    newFormValues.estimatedArrivalDate = busMaintenanceStay.get(
      'estimatedArrivalDate',
    );

    newFormValues.maintenanceStayStatus =
      formValues.maintenanceStayStatus.value;

    const completionDate = formValues.estimatedCompletionDate;
    const completionTime = formValues.estimatedCompletionTime;
    if (completionDate && completionTime) {
      const estimatedCompletionDateTime = new Date(
        completionDate.getFullYear(),
        completionDate.getMonth(),
        completionDate.getDate(),
        completionTime.getHours(),
        completionTime.getMinutes(),
        completionTime.getSeconds() || 0,
      );
      newFormValues.estimatedCompletionDate = tzNormalizeDate({
        date: estimatedCompletionDateTime,
        format: TIMESTAMP_FORMAT,
      });
    }

    dispatchPutBusMaintenanceStay(busMaintenanceStayId, newFormValues);
  };

  const generateInitialValues = ({
    registeredBusId,
    registeredBus,
    originCityId,
    originCity,
    maintenanceStayStatus,
    observation,
    estimatedArrivalDate,
    estimatedCompletionDate,
  }) => ({
    registeredBusId: registeredBusId && {
      value: registeredBusId,
      label: registeredBus.companyBusId,
    },
    originCityId: originCityId && {
      value: originCityId,
      label: originCity.name,
    },
    maintenanceStayStatus: BUS_MAINTENANCE_STATUS_TYPE[maintenanceStayStatus],
    observation,
    estimatedArrivalDate: estimatedArrivalDate
      ? tzNormalizeDate({ date: estimatedArrivalDate }).slice(0, 16)
      : null,

    estimatedCompletionDate: estimatedCompletionDate
      ? tzNormalizeDate({ date: estimatedCompletionDate }).slice(0, 16)
      : null,
  });

  let content;

  if (loading) content = <Loader />;
  else if (busMaintenanceStay.isEmpty())
    content = <NoDataResource returnPage={BUS_MAINTENANCE_STAY_PATH} />;
  else
    content = (
      <BusMaintenanceStayForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(busMaintenanceStay.toJS())}
        edit
      />
    );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Estancia de Mantenimiento por Bus"
      subtitle="Editar estancia de mantenimiento por bus"
      content={content}
    />
  );
};

EditBusMaintenanceStay.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearBusMaintenanceStay: PropTypes.func.isRequired,
  dispatchGetBusMaintenanceStay: PropTypes.func.isRequired,
  dispatchPutBusMaintenanceStay: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  busMaintenanceStay: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditBusMaintenanceStay.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchClearBusMaintenanceStay: clearBusMaintenanceStay,
  dispatchGetBusMaintenanceStay: getBusMaintenanceStay,
  dispatchPutBusMaintenanceStay: putBusMaintenanceStay,
};

const mapStateToProps = (
  { MechanicalMaintenanceUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...MechanicalMaintenanceUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Estancias de Mantenimiento por Bus',
      href: BUS_MAINTENANCE_STAY_PATH,
    },
    {
      text: 'Ver',
      href: `${BUS_MAINTENANCE_STAY_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  busMaintenanceStay: MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'current',
    'content',
  ]),
  loading: !MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditBusMaintenanceStay);
